/**
 * LangSwitcher — TopNav dropdown descriptor. Not a component; a shape the
 * shell can splice into its existing `utilities: TopNavigationProps.Utility[]`
 * array. This lets the shell own layout (position, spacing, aria-label) and
 * the i18n package own only the language state.
 *
 * If a shell ever wants to render the switcher elsewhere (e.g. an in-app
 * settings modal), it can call useLang() directly + build its own UI.
 */
import type { TopNavigationProps } from '@cloudscape-design/components/top-navigation';
import { useCallback } from 'react';
import { LANGUAGES, isLang } from './config.js';
import { useLang } from './provider.js';

/** Build a Cloudscape `menu-dropdown` Utility for the current language.
 *  Returns undefined only if no languages are registered (defensive; the
 *  static LANGUAGES array is never empty in practice). */
export function useLangSwitcherUtility(ariaLabel = 'Language'): TopNavigationProps.MenuDropdownUtility | undefined {
  const { lang, setLang } = useLang();
  const onItemClick = useCallback(
    ({ detail }: { detail: { id: string } }) => {
      if (isLang(detail.id)) setLang(detail.id);
    },
    [setLang],
  );

  const active = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];
  if (!active) return undefined;

  return {
    type: 'menu-dropdown',
    ariaLabel,
    // Show the ISO code (EN/RU/KZ) as the compact trigger label — takes
    // less TopNav real estate than the native word. The dropdown itself
    // shows the full native names.
    text: active.code.toUpperCase(),
    // Each row shows the native word first (what a native reader looks
    // for), with a subtle description of the English name for admins
    // navigating a language they don't read.
    items: LANGUAGES.map((l) => ({
      id: l.code,
      text: l.native,
      description: l.english,
    })),
    onItemClick,
  };
}
